import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Mic, Podcast, Users } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import { LINKS } from "../lib/content";

const TOPICS = [
  "From Pilots to Production: The Enterprise AI Adoption Playbook",
  "ROI Modeling for AI: TCO, NPV, and the Numbers CFOs Actually Trust",
  "RAG vs. Fine-Tuning: A Decision Framework for Enterprise Teams",
  "Value Engineering: How Solutions Consultants Win Enterprise Deals",
  "MCP and the Rise of Agentic Enterprise Workflows",
];

const FORMATS = [
  { icon: Mic, label: "Conference keynotes & panels" },
  { icon: Podcast, label: "Podcasts" },
  { icon: Users, label: "Private executive workshops (on-site & virtual)" },
];

export default function MediaKit() {
  useEffect(() => { document.title = "Media Kit - Yash Bhatt"; }, []);
  return (
    <main data-testid="media-kit-page" className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary">Media Kit</li>
            </ol>
          </nav>
          <MaskedLine>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-5">Media Kit · 2026</p>
          </MaskedLine>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.15] pb-2">
            <MaskedLine delay={0.15}>Yash Bhatt — <span className="text-gradient">AI Strategy & Enterprise Solutions Consultant</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-3xl">
              Yash Bhatt is an AI Strategy & Enterprise Solutions Consultant who helps enterprises turn AI ambition into production systems and CFO-defensible ROI.
            </p>
          </MaskedLine>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="/media-kit/yash-bhatt-media-kit.pdf" download data-testid="media-kit-download"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
              <Download size={15} /> Download Media Kit (PDF)
            </a>
            <a href={`mailto:${LINKS.email}?subject=Speaking Inquiry`} data-testid="media-kit-booking"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-border text-sm font-semibold hover:border-secondary/50 transition-colors duration-200">
              Booking · {LINKS.email}
            </a>
          </div>
        </div>
      </header>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-[280px_1fr] gap-12">
          <Reveal>
            <figure className="sticky top-24">
              <img src="/yash-bhatt.webp" alt="Yash Bhatt headshot"
                className="w-full aspect-square object-cover rounded-2xl border border-border" data-testid="media-kit-headshot" />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Official headshot · full-res in PDF</figcaption>
            </figure>
          </Reveal>

          <div className="space-y-14">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Short bio · ~60 words</p>
              <p className="text-base md:text-lg text-foreground/85 leading-relaxed">
                Yash Bhatt advises enterprises, SaaS companies, and AI startups on AI strategy, analytics, and intelligent automation. Currently a Solutions Consultant at Axion Ray, his career spans consultative research at Google, financial analytics at Oceaneering International and S&P Global, and independent advisory work. His focus: AI adoption that is measurable, governed, and genuinely useful — backed by TCO, NPV, and IRR rigor.
              </p>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Full bio · ~140 words</p>
              <p className="text-base text-foreground/85 leading-relaxed">
                Yash Bhatt is an AI Strategy & Enterprise Solutions Consultant based in Bangalore, working globally. Over six-plus years he has moved along one continuous thread: translating complex data into decisions organizations can act on. At S&P Global he delivered client-facing financial research; at Oceaneering International he automated forecasting and reporting, saving $120K annually; at Google he led consultative research that influenced 25 product roadmap and GTM decisions; and at Axion Ray he now leads AI solution consulting across enterprise deals, working hands-on with LLMs, RAG, MCP, and modern BI stacks. Yash speaks and writes on enterprise AI adoption, value engineering, and the gap between pilots and production. He holds a Post Graduate Program in Data Science & Engineering from Great Lakes Institute of Management and a PGDM from NMIMS.
              </p>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Speaking topics</p>
              <ol className="space-y-3">
                {TOPICS.map((t, i) => (
                  <li key={t} className="flex gap-4 items-start">
                    <span className="font-mono text-xs text-secondary mt-1 w-8 shrink-0">0{i + 1}</span>
                    <span className="text-base text-foreground/85 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ol>
            </Reveal>

            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Formats</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {FORMATS.map(({ icon: Icon, label }) => (
                  <div key={label} className="rounded-2xl border border-border bg-card p-5">
                    <Icon size={18} className="text-secondary" />
                    <p className="mt-3 text-sm text-foreground/85 leading-snug">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <div className="rounded-2xl border border-border bg-gradient-to-br from-violet-600/10 via-card to-blue-600/10 p-8 md:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-3">Booking</p>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">Available for keynotes, podcasts, and executive workshops.</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed"><a href={`mailto:${LINKS.email}`} className="text-secondary hover:underline">{LINKS.email}</a> · Fees available on request.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={LINKS.calendly} target="_blank" rel="noreferrer" data-testid="media-kit-cta"
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
                    Book a 30-min call <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary transition-colors duration-200">
                <ArrowLeft size={14} /> Back home
              </Link>
            </Reveal>
          </div>
        </div>
      </article>
    </main>
  );
}
